import { API_URL } from '../config';

class ReferralService {
  constructor() {
    this.baseURL = API_URL;
  }

  // Get authorization headers
  getAuthHeaders() {
    const bearerToken = localStorage.getItem('bearerToken');
    return {
      'Authorization': bearerToken,
      'Content-Type': 'application/json'
    };
  }

  // Handle API response
  async handleResponse(response) {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  }

  // Get user's referral code and basic statistics
  async getUserReferralCode() {
    try {
      const response = await fetch(`${this.baseURL}/referrals/code`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching referral code:', error);
      throw error;
    }
  }

  // Generate new referral code
  async generateNewReferralCode() {
    try {
      const response = await fetch(`${this.baseURL}/referrals/code/generate`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error generating referral code:', error);
      throw error;
    }
  }

  // Get referral history with pagination
  async getReferralHistory(page = 1, perPage = 20) {
    try {
      const response = await fetch(`${this.baseURL}/referrals/history?page=${page}&per_page=${perPage}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching referral history:', error);
      throw error;
    }
  }

  // Apply referral code
  async applyReferralCode(referralCode) {
    try {
      const response = await fetch(`${this.baseURL}/referrals/apply`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ referral_code: referralCode })
      });
      
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error applying referral code:', error);
      throw error;
    }
  }

  // Get detailed statistics
  async getDetailedStatistics() {
    try {
      const response = await fetch(`${this.baseURL}/referrals/statistics`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }

  // Validate referral code (public)
  async validateReferralCode(referralCode) {
    try {
      const response = await fetch(`${this.baseURL}/referrals/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ referral_code: referralCode })
      });
      
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error validating referral code:', error);
      throw error;
    }
  }

  // Get referral leaderboard
  async getLeaderboard(limit = 10) {
    try {
      const response = await fetch(`${this.baseURL}/referrals/leaderboard?limit=${limit}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      throw error;
    }
  }

  // Copy referral link to clipboard
  copyReferralLink(referralCode) {
    const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;
    
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(referralLink);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = referralLink;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return Promise.resolve();
      } catch (err) {
        document.body.removeChild(textArea);
        return Promise.reject(err);
      }
    }
  }

  // Share referral link via Web Share API
  async shareReferralLink(referralCode, userName) {
    const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;
    const shareData = {
      title: 'Join RoamBis with my referral!',
      text: `Hey! Join RoamBis using my referral code and get amazing eSIM deals. Use code: ${referralCode}`,
      url: referralLink
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return true;
      } catch (err) {
        console.log('Error sharing:', err);
        return false;
      }
    } else {
      // Fallback to copying
      await this.copyReferralLink(referralCode);
      return false;
    }
  }
}

// Create a singleton instance
const referralService = new ReferralService();

// Export both the class and the instance
export { ReferralService };
export default referralService;
